/**
 * informantFirestorePaths.js
 *
 * Resolves where modular vs ADHD informant data lives in Firestore.
 *
 * ADHD informants (legacy): monolithic doc at patients/{informantUid} with formData.
 * Modular informants (Adult Autism, CYP ADHD, future pathways): profile at
 * informants/{informantUid}, forms in the pathway-namespaced assignment tree
 * beneath patients/{patientUid}.
 *
 * Routing decisions use the assessment module registry so adding a new modular
 * pathway does not require changes here.
 */

import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { AUTISM_INFORMANT_FORM_KEYS } from '../constants/adultAutismFormKeys';
import { getInformantFormKeys } from '../constants/informantFormKeys';
import { normalizeStoredProgressToPercentage } from './formProgress';
import {
  buildAutismSubcollectionQuery,
} from './autismFirestoreQueries';
import {
  isModularModule,
  getModuleCollection,
  getModuleAssessmentDocId,
  getModuleActiveAssignmentKey,
} from '../constants/assessmentModules';

/** Re-export for callers that already import from this module. */
export { AUTISM_INFORMANT_FORM_KEYS };

export const AUTISM_ASSESSMENT_DOC_ID = 'assessment_001';

/**
 * Resolve a Firestore Timestamp (or plain seconds value) to milliseconds for sorting.
 */
function assignedAtToMillis(assignedAt) {
  if (!assignedAt) return 0;
  if (typeof assignedAt.toMillis === 'function') return assignedAt.toMillis();
  if (typeof assignedAt.seconds === 'number') return assignedAt.seconds * 1000;
  if (typeof assignedAt === 'number') return assignedAt;
  return 0;
}

/**
 * Return informant IDs in assignment order (first assigned = index 0).
 * Used by the autism patient overview so "Informant 1" matches setup order.
 */
export function getOrderedAutismInformantIds(informantsMap = {}) {
  return Object.entries(informantsMap)
    .sort(([, a], [, b]) => assignedAtToMillis(a?.assignedAt) - assignedAtToMillis(b?.assignedAt))
    .map(([informantId]) => informantId);
}

/**
 * Resolve the linked patient ID for a modular informant profile.
 * Reads from the activeAssignments sub-key for the given module.
 * Falls back to adult_autism for backward compatibility when no module is specified.
 *
 * @param {object} profile - Informant profile document data
 * @param {string} [assessmentModule='adultAutism'] - Module key from claims
 */
export function resolveModularInformantLinkedPatientId(profile, assessmentModule = 'adultAutism') {
  const assignmentKey = getModuleActiveAssignmentKey(assessmentModule) || 'adult_autism';
  const assignments = profile?.activeAssignments?.[assignmentKey];
  if (!assignments || typeof assignments !== 'object') return null;

  const activeEntries = Object.entries(assignments).filter(([, entry]) => {
    if (!entry || typeof entry !== 'object') return false;
    return entry.status !== 'deactivated';
  });

  if (activeEntries.length === 0) {
    const allIds = Object.keys(assignments);
    return allIds.length > 0 ? allIds[0] : null;
  }

  activeEntries.sort(([, a], [, b]) => assignedAtToMillis(a?.assignedAt) - assignedAtToMillis(b?.assignedAt));
  return activeEntries[0][0];
}

/**
 * Backward-compatible alias — resolves adult_autism assignments by default.
 */
export function resolveAutismInformantLinkedPatientId(profile) {
  return resolveModularInformantLinkedPatientId(profile, 'adultAutism');
}

/**
 * Read patient first name for template injection — prefers live assignment data,
 * falls back to empty string when not yet known.
 *
 * @param {object} profile - Informant profile document data
 * @param {string} linkedPatientId
 * @param {string} [assessmentModule='adultAutism'] - Module key from claims
 */
export function resolvePatientFirstNameForInformant(profile, linkedPatientId, assessmentModule = 'adultAutism') {
  const assignmentKey = getModuleActiveAssignmentKey(assessmentModule) || 'adult_autism';
  const fromAssignment = profile?.activeAssignments?.[assignmentKey]?.[linkedPatientId]?.patientFirstName;
  if (typeof fromAssignment === 'string' && fromAssignment.trim()) {
    return fromAssignment.trim();
  }
  return '';
}

/**
 * Resolve the Firestore document reference for a service user's own profile,
 * using auth claims to deterministically pick the correct collection.
 *
 * Modular informants (Adult Autism, CYP ADHD, etc.) live in informants/{uid};
 * everyone else (ADHD patients, ADHD informants, modular patients) lives in
 * patients/{uid}.
 *
 * @param {string} uid - The user's Firebase Auth UID
 * @param {object|null} claims - Firebase custom claims (from useAuth or getIdTokenResult)
 * @returns {import('firebase/firestore').DocumentReference}
 */
export function getServiceUserProfileDocRef(uid, claims) {
  if (claims?.userType === 'Informant' && isModularModule(claims?.assessmentModule)) {
    return doc(db, 'informants', uid);
  }
  return doc(db, 'patients', uid);
}

/**
 * Whether a patient.informants.{uid} map entry points at the informants/ collection
 * (modular informant storage). Checks collectionRef first, then falls back to
 * the assessmentModule field via the registry.
 */
export function isModularInformantEntry(entry) {
  if (!entry || typeof entry !== 'object') return false;
  if (entry.collectionRef === 'informants') return true;
  return isModularModule(entry.assessmentModule);
}

/**
 * Backward-compatible alias for callers that import isAutismInformantEntry.
 */
export const isAutismInformantEntry = isModularInformantEntry;

/**
 * Whether the patient is on the Adult Autism assessment pathway.
 */
export function isAutismPatient(patientDataModel) {
  return (patientDataModel?.assessmentModule || 'adultAdhd') === 'adultAutism';
}

/**
 * Whether the patient is on any modular (subcollection-based) pathway.
 */
export function isModularPatient(patientDataModel) {
  return isModularModule(patientDataModel?.assessmentModule);
}

/**
 * Firestore path segments for an informant profile document.
 */
export function getInformantProfileDocRef(informantId, entry) {
  if (isAutismInformantEntry(entry)) {
    return doc(db, 'informants', informantId);
  }
  return doc(db, 'patients', informantId);
}

/**
 * Collection ref for modular informant formInstances under a patient assignment.
 * Defaults to adult_autism for backward compatibility.
 *
 * @param {string} informantId
 * @param {string} patientId
 * @param {string} [assessmentModule='adultAutism'] - Module key
 */
export function getModularInformantFormInstancesCollectionRef(informantId, patientId, assessmentModule = 'adultAutism') {
  const col = getModuleCollection(assessmentModule) || 'adult_autism';
  const docId = getModuleAssessmentDocId(assessmentModule) || AUTISM_ASSESSMENT_DOC_ID;
  return collection(
    db,
    'informants',
    informantId,
    'patients',
    patientId,
    col,
    docId,
    'formInstances',
  );
}

/**
 * Backward-compatible alias — resolves adult_autism paths by default.
 */
export function getAutismInformantFormInstancesCollectionRef(informantId, patientId) {
  return getModularInformantFormInstancesCollectionRef(informantId, patientId, 'adultAutism');
}

/**
 * Compute aggregate progress across an explicit list of informant form keys.
 *
 * The key list must be passed in because the denominator is pathway- and
 * role-specific: a CYP ADHD guardian is measured over nine forms, a school rep
 * over two, and an autism informant over seven. Averaging a school rep over the
 * guardian's nine forms would cap them at 22% however much they complete.
 *
 * @param {object} formInstances - map of formKey -> stored form instance
 * @param {string[]} formKeys - the keys actually assigned to this informant
 */
export function computeModularInformantProgress(formInstances, formKeys) {
  const keys = Array.isArray(formKeys) ? formKeys : [];
  const formProgress = {};
  let totalProgress = 0;
  let startedCount = 0;
  let completedCount = 0;

  keys.forEach((formKey) => {
    const fi = formInstances?.[formKey];
    const rawProgress = fi?.progress ?? 0;
    const status = fi?.status || 'notStarted';
    const percentage = normalizeStoredProgressToPercentage(rawProgress);

    formProgress[formKey] = {
      formKey,
      percentage,
      status,
      completedAt: status === 'completed' ? fi?.updatedAt : null,
      lastUpdated: fi?.updatedAt || null,
    };

    totalProgress += percentage / 100;
    if (percentage > 0 || status === 'inProgress') startedCount += 1;
    if (status === 'completed' || percentage >= 100) completedCount += 1;
  });

  // Guard against an empty key list (unknown module): avoid NaN from 0/0, and
  // never report "complete" for an informant with no forms assigned.
  const averageProgress = keys.length > 0 ? totalProgress / keys.length : 0;

  return {
    hasStarted: startedCount > 0,
    hasCompleted: keys.length > 0 && completedCount === keys.length,
    averageProgress,
    formProgress,
  };
}

/**
 * Compute aggregate progress for autism informant history forms.
 *
 * Retained as the autism-specific entry point so existing call sites keep
 * working; new modular pathways should call computeModularInformantProgress
 * with their own resolved key list.
 */
export function computeAutismInformantProgress(formInstances) {
  return computeModularInformantProgress(formInstances, AUTISM_INFORMANT_FORM_KEYS);
}

/**
 * Load an informant profile + form data for display in management / overview UIs.
 *
 * @param {object} [accessOptions] - Optional; when omitted, derived from auth + profile.
 * @param {string} accessOptions.organisation
 * @param {string} accessOptions.clinicianUid
 * @param {boolean} accessOptions.isOrgWideAccess
 */
export async function fetchEnrichedInformantData(informantId, patientId, mapEntry, accessOptions = null) {
  const profileRef = getInformantProfileDocRef(informantId, mapEntry);
  const profileSnap = await getDoc(profileRef);

  if (!profileSnap.exists()) {
    return null;
  }

  const profile = profileSnap.data();

  if (isModularInformantEntry(mapEntry)) {
    const entryModule = mapEntry?.assessmentModule || 'adultAutism';
    const col = getModuleCollection(entryModule) || 'adult_autism';
    const docId = getModuleAssessmentDocId(entryModule) || AUTISM_ASSESSMENT_DOC_ID;

    const resolvedAccess = accessOptions || {
      organisation: profile.organisation || '',
      clinicianUid: auth.currentUser?.uid || '',
      isOrgWideAccess: false,
    };

    const formsSnap = await getDocs(
      buildAutismSubcollectionQuery(
        db,
        [
          'informants', informantId, 'patients', patientId,
          col, docId, 'formInstances',
        ],
        resolvedAccess,
      ),
    );
    const formInstances = {};
    formsSnap.forEach((d) => {
      formInstances[d.id] = d.data();
    });

    // Resolve the informant's ACTUAL form list from (module, role) — a CYP
    // school rep has 4 forms and a CYP guardian 11, so averaging over
    // autism's 7 keys would render progress wrong for every CYP informant.
    const informantRole = mapEntry?.informantRole || profile?.informantRole;
    const assignedFormKeys = getInformantFormKeys(entryModule, informantRole);
    const { hasStarted, hasCompleted, formProgress } = computeModularInformantProgress(
      formInstances,
      assignedFormKeys,
    );

    return {
      ...profile,
      formInstances,
      formData: null,
      isAutismInformant: true,
      isModularInformant: true,
      hasStarted,
      hasCompleted,
      formProgress,
      lifeStages: [],
    };
  }

  const formData = profile.formData || {};
  return {
    ...profile,
    formData,
    formInstances: null,
    isAutismInformant: false,
  };
}
